import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { REMOTE_PERMISSIONS } from '@backrs_ui/common-ui';

import { type ShellMode } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { useSessionToolTitle } from '@backrs_ui/grow-ui/src/hooks/useSessionToolTitle';
import {
  type ResourcesContent,
  isStructuredSession,
  type StructuredSessionId,
} from '@backrs_ui/grow-ui/src/models/models';
import { useHomeAudience } from '@backrs_ui/grow-ui/src/pages/Home/homeAudience';
import {
  markTabRead,
  removeNonFeaturedResources,
  removeResourcesByKeys,
  restoreResources,
  selectArtifact,
  selectHighlightedNugget,
  selectResourcesContent,
  selectUnreadTabs,
  setHighlightedNugget,
  setToolTriggered,
} from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import { selectSelectedMemberSolutionSession } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/memberSolutionSession';
import { useRemoveSessionResourcesMutation } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import {
  FEATURE_FLAGS,
  selectIsFeatureFlagEnabled,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  createSessionAction,
  getActiveLubavSession,
  getActiveLubavSubsession,
  getDisplayedSession,
  getLoadingSessionData,
  getPreviewedSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { getSessionStatusEntry } from '@backrs_ui/grow-ui/src/redux/sessionStatuses/sessionStatuses';
import { type CustomTabId } from '@backrs_ui/grow-ui/src/redux/tabs/customTabIds';
import { getTabKey, type TabItem } from '@backrs_ui/grow-ui/src/redux/tabs/tabKey';
import {
  closeCustomTab,
  selectOpenCustomTabs,
  selectSelectedTabKey,
  setSelectedTabKey,
} from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';

import { composeTabs, EMPTY_BOX_TYPES } from './composeTabs';
import { type BoxType } from './InfoBox.config';
import { type InfoBoxContentProps } from './InfoBoxContent';
import { getInfoBoxEmptyState } from './infoBoxEmptyStates';
import { type ShellContentTabsProps } from './ShellContentTabs';
import { useSortedResources } from './useSortedResources';

const NO_STATIC_TABS: readonly CustomTabId[] = [];

// What the FEATURED tab holds for a previewed session. Any saved output the previewed tool has
// stays out until the user enters it; every content prop here reads from the active session.
const BANNER_ONLY_CONTENT: InfoBoxContentProps = {
  currentBox: undefined,
  sortedContent: [],
  isFeaturedTab: true,
  isSuggestedTab: false,
  artifact: undefined,
  highlightedNugget: null,
};

type UseShellResourceTabsProps = Readonly<{
  shellMode: ShellMode;
}>;

export type UseShellResourceTabsReturn = Readonly<{
  hasResourceTabs: boolean;
  showResourceContent: boolean;
  isContentReady: boolean;
  isLoadingExistingOutput: boolean;
  selectedBoxType: BoxType | undefined;
  selectedCustomTabId: CustomTabId | undefined;
  tabsProps: ShellContentTabsProps;
  contentProps: InfoBoxContentProps;
}>;

export const useShellResourceTabs = ({
  shellMode,
}: UseShellResourceTabsProps): UseShellResourceTabsReturn => {
  const homeAudience = useHomeAudience();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const activeSession = useAppSelector(getActiveLubavSession);
  const previewedSession = useAppSelector(getPreviewedSession);
  const displayedSession = useAppSelector(getDisplayedSession);
  const isStructured = isStructuredSession(activeSession);
  // Only a structured session has a tool title; a solution session keeps the tab's config label.
  const sessionToolTitle = useSessionToolTitle(
    isStructuredSession(displayedSession) ? displayedSession : null,
  );
  const subsession = useAppSelector(getActiveLubavSubsession);
  const openCustomTabs = useAppSelector(selectOpenCustomTabs);
  const selectedTabKey = useAppSelector(selectSelectedTabKey);
  const resourcesContent = useAppSelector((state) => selectResourcesContent(state, activeSession));
  const artifact = useAppSelector((state) => selectArtifact(state, activeSession));
  const highlightedNugget = useAppSelector((state) =>
    selectHighlightedNugget(state, activeSession),
  );
  const unreadTabs = useAppSelector((state) => selectUnreadTabs(state, activeSession));
  const {
    streaming: isStreaming,
    loading: isTyping,
    officeHoursMessages,
    sessionId,
  } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);
  const { toolTriggered } = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData,
  );
  const loadingSessionData = useAppSelector(getLoadingSessionData);
  const sessionStatus = useAppSelector((state) =>
    isStructured
      ? getSessionStatusEntry(state, activeSession as StructuredSessionId)?.status
      : undefined,
  );
  const [removeResources] = useRemoveSessionResourcesMutation();
  const selectedSolutionSession = useAppSelector(selectSelectedMemberSolutionSession);
  const isCoachingEnabled = useAppSelector(selectIsFeatureFlagEnabled(FEATURE_FLAGS.COACHING));
  const canAccessCoaches = useHasPermission(REMOTE_PERMISSIONS.ACCESS_COACHES);
  const coachingTabsAllowed = isCoachingEnabled && canAccessCoaches;

  const previousBoxTypesRef = useRef<readonly BoxType[]>(EMPTY_BOX_TYPES);
  const { tabs, availableBoxTypes, selectedIndex, infoBoxConfig, tabsWithContent, staticTabs } =
    composeTabs({
      shell: {
        layout: shellMode.layout ?? 'page',
        staticTabs: shellMode.staticTabs ?? NO_STATIC_TABS,
        openCustomTabs,
        selectedTabKey,
        coachingTabsAllowed,
      },
      session: {
        activeSession,
        previewedSession,
        subsession,
        hasUserMessages: officeHoursMessages.length > 1,
        isLubavActive: isStreaming || isTyping,
      },
      content: { resources: resourcesContent, artifact },
      previousAvailableBoxTypes: previousBoxTypesRef.current,
    });

  previousBoxTypesRef.current = availableBoxTypes;

  const selectedItem = tabs[selectedIndex];
  const selectedBoxType = selectedItem?.kind === 'resource' ? selectedItem.boxType : undefined;
  const selectedCustomTabId = selectedItem?.kind === 'custom' ? selectedItem.tabId : undefined;

  // Only the first load defers to composeTabs' default tab (FEATURED in a structured session);
  // any later move to a different page, including back/forward, focuses that page. We compare
  // pathnames because useNavigationType can't tell a fresh load from a back nav (both are POP).
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevPathnameRef.current === location.pathname) return;

    const isNavigation = prevPathnameRef.current !== null;

    prevPathnameRef.current = location.pathname;
    dispatch(setSelectedTabKey(isNavigation ? 'page-content' : null));
  }, [location.pathname, dispatch]);

  // when entering a subsession, drop the explicit selection so composeTabs picks a fresh default
  useEffect(() => {
    if (subsession) dispatch(setSelectedTabKey(null));
  }, [subsession, dispatch]);

  // mark a tab read once the user lands on it
  useEffect(() => {
    if (!selectedBoxType || !unreadTabs.includes(selectedBoxType)) return;

    dispatch(createSessionAction(markTabRead, activeSession)(selectedBoxType));
  }, [selectedBoxType, unreadTabs, activeSession, dispatch]);

  const customTabLabels = useMemo(
    () =>
      selectedSolutionSession
        ? { memberSolutionSession: selectedSolutionSession.title }
        : undefined,
    [selectedSolutionSession],
  );

  const sortedContent = useSortedResources(
    selectedBoxType ? infoBoxConfig[selectedBoxType] : undefined,
    resourcesContent,
    selectedBoxType,
  );

  // a tool was triggered and content has now arrived — clear the loading flag
  useEffect(() => {
    if (toolTriggered && sortedContent.length > 0) {
      dispatch(createSessionAction(setToolTriggered, activeSession)(false));
    }
  }, [toolTriggered, sortedContent.length, activeSession, dispatch]);

  const handleCloseTab = useCallback(
    async (tab: TabItem) => {
      if (getTabKey(tab) === selectedTabKey) {
        // In a structured session, closing a tab returns to the session's FEATURED output
        // tab rather than whatever page was underneath.
        if (isStructured) {
          dispatch(setSelectedTabKey(getTabKey({ kind: 'resource', boxType: 'FEATURED' })));
        } else {
          const closedIndex = tabs.indexOf(tab);
          const neighbor = tabs[closedIndex - 1] ?? tabs[closedIndex + 1];

          dispatch(setSelectedTabKey(neighbor ? getTabKey(neighbor) : null));
        }
      }

      if (tab.kind === 'custom') {
        dispatch(closeCustomTab(tab.tabId));

        return;
      }

      if (tab.kind !== 'resource') return;

      const config = infoBoxConfig[tab.boxType];

      if (!config || !sessionId) return;

      // Closing SUGGESTED only discards non-featured items so named tabs keep their content.
      const isSuggested = tab.boxType === 'SUGGESTED';
      const resourceKeys = config.resources;
      // Optimistic remove + rollback: snapshot first so a backend failure can restore the tab.
      const snapshot: Partial<ResourcesContent> = resourcesContent
        ? Object.fromEntries(
            resourceKeys
              .filter((key) => resourcesContent[key] !== undefined)
              .map((key) => [key, resourcesContent[key]]),
          )
        : {};

      if (isSuggested) {
        dispatch(createSessionAction(removeNonFeaturedResources, activeSession)(resourceKeys));
      } else {
        dispatch(createSessionAction(removeResourcesByKeys, activeSession)(resourceKeys));
      }

      dispatch(createSessionAction(markTabRead, activeSession)(tab.boxType));

      try {
        await removeResources({
          session_id: sessionId,
          resource_keys: resourceKeys,
          only_non_featured: isSuggested,
        }).unwrap();
      } catch (error) {
        console.error('Failed to remove session resources', error);
        // Rollback so the stale tab doesn't reappear on next page load.
        dispatch(createSessionAction(restoreResources, activeSession)(snapshot));
      }
    },
    [
      tabs,
      selectedTabKey,
      isStructured,
      infoBoxConfig,
      sessionId,
      resourcesContent,
      activeSession,
      removeResources,
      dispatch,
    ],
  );

  const bannerOnly = previewedSession !== null && selectedBoxType === 'FEATURED';

  return {
    hasResourceTabs:
      availableBoxTypes.length > 0 || openCustomTabs.length > 0 || staticTabs.length > 0,
    showResourceContent: selectedItem?.kind === 'resource' || selectedItem?.kind === 'custom',
    // Only show the output loader while a tool is actually generating output (and nothing's
    // there yet) -- not while a session is loading or being initiated.
    isContentReady: bannerOnly || !!artifact || sortedContent.length > 0 || !toolTriggered,
    // Resuming a finished session whose output isn't loaded yet.
    isLoadingExistingOutput:
      loadingSessionData === activeSession && sessionStatus === 'complete' && !artifact,
    selectedBoxType,
    selectedCustomTabId,
    tabsProps: {
      selectedTab: selectedIndex,
      onTabChange: (tabIndex: number) => {
        const tab = tabs[tabIndex];

        if (tab) dispatch(setSelectedTabKey(getTabKey(tab)));

        dispatch(createSessionAction(setHighlightedNugget, activeSession)(null));
      },
      onCloseTab: handleCloseTab,
      tabs,
      infoBoxConfig,
      unreadTabs,
      tabsWithContent,
      staticTabs,
      featuredTabLabel: sessionToolTitle,
      customTabLabels,
    },
    contentProps: bannerOnly
      ? BANNER_ONLY_CONTENT
      : {
          currentBox: selectedBoxType ? infoBoxConfig[selectedBoxType] : undefined,
          sortedContent,
          isFeaturedTab: selectedBoxType === 'FEATURED',
          isSuggestedTab: selectedBoxType === 'SUGGESTED',
          artifact,
          highlightedNugget,
          emptyStateContent: getInfoBoxEmptyState({
            boxType: selectedBoxType,
            audience: homeAudience,
            pathname: location.pathname,
            inSubsession: !!subsession,
            inStructuredSession: isStructured,
          }),
        },
  };
};
